import { useCallback, useRef, useState } from 'react';
import {
  clearOrganizationTrial,
  convertOrganizationTrial,
  setOrganizationTrial,
} from '../../services/admin';
import { inclusiveDateToStartIso, utcDateInputValue } from '../../utils/calendarTime';
import { trialFailureMessage, type TrialAction } from './organizationTrial';
import type {
  OrganizationTrialFields,
  OrganizationTrialResponse,
} from '../../types/admin';

/** Which trial dialog is open, if any. */
export type TrialModalKind = 'dates' | 'convert' | 'clear';

/**
 * What a failure is about, which decides how it is presented.
 *
 * `dates` is something wrong with what was typed, so it belongs on the field itself where it can
 * carry `aria-invalid` and be read out with the input. `request` is the server refusing, which no
 * single field is responsible for, so it goes to a `role="alert"` of its own.
 */
export type TrialErrorTarget = 'dates' | 'request';

/** Which of the two date fields a `dates` failure is actually about. */
export type TrialErrorField = 'start' | 'end';

interface TrialFailure {
  message: string;
  target: TrialErrorTarget;
  field?: TrialErrorField;
  /**
   * What makes this rejection a different one from the last. Two identical refusals are two
   * events, and a consumer comparing failures by value cannot tell them apart.
   */
  seq: number;
}

/**
 * Which dialog each call belongs to. The two vocabularies differ by a word, since one dialog both
 * starts and extends a trial, and the in-flight flag has to be about the dialog rather than the
 * call so that it can never describe a dialog other than its own.
 */
const MODAL_FOR_ACTION: Record<TrialAction, TrialModalKind> = {
  set: 'dates',
  convert: 'convert',
  clear: 'clear',
};

interface UseOrganizationTrialOptions {
  organizationId: string | undefined;
  /** Called with the API response so the caller can fold it into local state without a refetch. */
  onTrialChanged: (result: OrganizationTrialResponse) => void;
}

/**
 * Owns the trial flow for one organization: which dialog is open, the two date fields, the
 * in-flight flag and whatever went wrong.
 *
 * **Dates are sent as UTC midnight of the day that was picked**, via the same
 * `inclusiveDateToStartIso` the calendar uses, and read back with `utcDateInputValue`. That pair
 * round-trips exactly: the day an admin types is the day the field shows them afterwards. The
 * field is a calendar day and the column is an instant, so something has to fix the zone, and
 * picking the viewer's would mean two admins in different places setting the same trial to
 * different instants.
 *
 * **Every rejection names the field it is about.** Both rules here are about the pair, but each
 * one still has a control that is wrong: with the start blank and the end filled, the thing to
 * fix is the start. Reporting that against the end field would mark the wrong input invalid and
 * put the reason nowhere near the empty box.
 */
export const useOrganizationTrial = ({
  organizationId,
  onTrialChanged,
}: UseOrganizationTrialOptions) => {
  const [modal, setModal] = useState<TrialModalKind | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  /** Which dialog has a request out, which is not necessarily the one on screen. */
  const [pendingModal, setPendingModal] = useState<TrialModalKind | null>(null);
  const [failure, setFailure] = useState<TrialFailure | null>(null);

  /**
   * Identifies the run allowed to write. Leaving a dialog bumps it, so a response settling after
   * that can tell nobody is waiting for it any more.
   */
  const runIdRef = useRef(0);
  const failureSeqRef = useRef(0);

  /**
   * Busy describes the dialog that started the request and no other.
   *
   * Derived rather than stored so it cannot outlive that dialog: an admin who backed out of a slow
   * save and opened another trial dialog used to find it already disabled and reading "Saving...",
   * having pressed nothing.
   */
  const loading = modal !== null && pendingModal === modal;

  /**
   * Records a failure as its own event.
   *
   * Pressing Save twice on the same bad dates yields the same message about the same field, so
   * without the sequence number the second press changes nothing observable: focus does not move,
   * and the field error is a plain span with no `role="alert"` to announce itself. The dialog
   * simply reads as frozen.
   *
   * **Only the dates validation needs this**, so do not thread it into the other two dialogs for
   * symmetry. A server refusal goes through `run()`, which clears the failure before the request,
   * so its `role="alert"` really does unmount and remount between two identical refusals and is
   * announced again on its own.
   */
  const reportFailure = useCallback((next: Omit<TrialFailure, 'seq'>) => {
    setFailure({ ...next, seq: ++failureSeqRef.current });
  }, []);

  /** Gives up on whatever is in flight: its response no longer has a dialog to report to. */
  const abandonRun = useCallback(() => {
    runIdRef.current += 1;
    setPendingModal(null);
  }, []);

  /*
    Every one of these changes which dialog is on screen, so every one of them abandons the run
    that was reporting to the previous one. Keeping that rule in one place is what stops a response
    from ever landing on a dialog that did not ask for it.
  */

  /** Opens the date dialog, seeded with the window the organization already has. */
  const openDates = useCallback(
    (existing: OrganizationTrialFields) => {
      abandonRun();
      setFailure(null);
      setStartDate(utcDateInputValue(existing.trialStartedAt ?? ''));
      setEndDate(utcDateInputValue(existing.trialEndsAt ?? ''));
      setModal('dates');
    },
    [abandonRun]
  );

  const openConvert = useCallback(() => {
    abandonRun();
    setFailure(null);
    setModal('convert');
  }, [abandonRun]);

  const openClear = useCallback(() => {
    abandonRun();
    setFailure(null);
    setModal('clear');
  }, [abandonRun]);

  const close = useCallback(() => {
    abandonRun();
    setModal(null);
    setFailure(null);
  }, [abandonRun]);

  /**
   * Runs one trial call and reports it back to the dialog that started it, and to nothing else.
   *
   * **The run token is what ties a settling response to its own dialog.** `ConfirmModal` keeps
   * Escape and Cancel live while a request is in flight on purpose, so backing out is a supported
   * gesture and a slow response can land long after the admin has opened a different dialog.
   * Writing then closes and mislabels the dialog they are actually looking at, and on the success
   * path it recorded a conversion, the one-way door this feature warns about twice, after they had
   * explicitly backed out of it.
   *
   * **An abandoned run therefore writes nothing at all, its result included.** The request was
   * already sent and the server has already acted on it, so the panel can sit behind the truth
   * until the next load. That is the cheaper wrong: stale dates are visibly recoverable by
   * reloading, whereas rewriting the screen underneath an open dialog is neither visible nor
   * something the admin asked for.
   *
   * What makes it survivable is copy that already exists. An admin acting on the stale panel and
   * pressing Mark as converted again gets the 409 back as "This organization is already marked as
   * converted", so the screen corrects itself through a sentence written for exactly this.
   */
  const run = useCallback(
    async (action: () => Promise<OrganizationTrialResponse>, kind: TrialAction) => {
      const runId = ++runIdRef.current;
      setPendingModal(MODAL_FOR_ACTION[kind]);
      setFailure(null);

      try {
        const result = await action();
        if (runIdRef.current !== runId) return;
        onTrialChanged(result);
        setModal(null);
      } catch (err: unknown) {
        // Logged whether or not anyone is still waiting, because the call really was refused.
        console.error(`Failed to ${kind} the trial:`, err);
        if (runIdRef.current !== runId) return;
        // The dialog stays open with the reason on it. A 400 in particular is only fixable from
        // here, and closing would throw away the dates the admin just typed.
        reportFailure({ message: trialFailureMessage(err, kind), target: 'request' });
      } finally {
        // Only the current run may clear the flag. A stale one clearing it would report the dialog
        // that is open now as idle when it is not.
        if (runIdRef.current === runId) setPendingModal(null);
      }
    },
    [onTrialChanged, reportFailure]
  );

  const confirmDates = useCallback(async () => {
    if (!organizationId) return;

    const start = startDate.trim();
    const end = endDate.trim();

    const rejectDates = (message: string, field: TrialErrorField) =>
      reportFailure({ message, target: 'dates', field });

    /*
      Checked here as well as on the server, because these two are the whole content of the dialog
      and the admin can see both. A round trip to be told the end is before the start is a worse
      version of a sentence we can write immediately.
    */
    if (!start || !end) {
      rejectDates(
        'Enter both dates. To take the trial off this organization, use Clear trial.',
        start ? 'end' : 'start'
      );
      return;
    }

    const startsAt = inclusiveDateToStartIso(start);
    const endsAt = inclusiveDateToStartIso(end);

    // inclusiveDateToStartIso yields "" for anything it cannot parse, and "" would reach the API
    // as a model-binding 400 rather than as the sentence below.
    if (!startsAt || !endsAt) {
      rejectDates(
        'Enter the dates as real calendar dates.',
        startsAt ? 'end' : 'start'
      );
      return;
    }

    // The API requires the end to be strictly after the start, so a same-day window is refused
    // here rather than travelling to be refused there.
    if (end <= start) {
      rejectDates('The trial has to end after it starts.', 'end');
      return;
    }

    await run(
      () =>
        setOrganizationTrial(organizationId, {
          trialStartedAt: startsAt,
          trialEndsAt: endsAt,
        }),
      'set'
    );
  }, [organizationId, startDate, endDate, run, reportFailure]);

  const confirmConvert = useCallback(async () => {
    if (!organizationId) return;
    await run(() => convertOrganizationTrial(organizationId), 'convert');
  }, [organizationId, run]);

  const confirmClear = useCallback(async () => {
    if (!organizationId) return;
    await run(() => clearOrganizationTrial(organizationId), 'clear');
  }, [organizationId, run]);

  return {
    modal,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loading,
    error: failure?.message ?? null,
    errorTarget: failure?.target ?? null,
    errorField: failure?.field ?? null,
    errorSeq: failure?.seq ?? null,
    openDates,
    openConvert,
    openClear,
    close,
    confirmDates,
    confirmConvert,
    confirmClear,
  };
};
